import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '..';

export const selectAuth = (state: RootState) => state.auth;

export const selectEnableAuth = createSelector(
  selectAuth,
  (auth) => auth.isAuthorized
);

export const selectCurrentUser = createSelector(
  selectAuth,
  (auth) => auth.currentUser
);

export const selectIsSuperAdmin = createSelector(
  selectCurrentUser,
  (user) => user?.role === 'super_admin'
);

export const selectIsCompanyAdmin = createSelector(
  selectCurrentUser,
  (user) => user?.role === 'company_admin'
);

export const selectIsContentManager = createSelector(
  selectCurrentUser,
  (user) => user?.role === 'content_manager'
);

export const selectCanManageUsers = createSelector(
  selectCurrentUser,
  (user) => user?.role === 'super_admin' || user?.role === 'company_admin'
);

export const selectCanDeleteContent = createSelector(
  selectCurrentUser,
  (user) => user?.role === 'super_admin' || user?.role === 'company_admin'
);

export const selectCurrentCompany = createSelector(
  selectCurrentUser,
  (user) => user?.company ?? null
);

/**
 * Mirrors CompanyPolicy::create on the backend — only super_admin can
 * create a company. Used to gate the "+ Add New Company" affordance in
 * pickers so tenant users don't see an option they'd get a 403 on.
 */
export const selectCanCreateCompany = createSelector(
  selectCurrentUser,
  (user) => user?.role === 'super_admin'
);

/**
 * Mirrors PlaylistPolicy::create on the backend — any active user with a
 * recognised role (super_admin / company_admin / content_manager) may create
 * a playlist. Used to gate the "+ Create New Playlist" affordance so users
 * without permission don't see an option they'd get a 403 on.
 */
export const selectCanCreatePlaylist = createSelector(
  selectCurrentUser,
  (user) =>
    !!user?.is_active &&
    ['super_admin', 'company_admin', 'content_manager'].includes(user?.role ?? '')
);

/**
 * Mirrors PlaylistPolicy::update — any active user with a recognised role may
 * edit playlists (ownership is enforced server-side and, inside a group, every
 * playlist already belongs to the viewer's company). Gates the row "Edit
 * playlist" affordance in the Playlist Group editor.
 */
export const selectCanEditPlaylist = createSelector(
  selectCurrentUser,
  (user) =>
    !!user?.is_active &&
    ['super_admin', 'company_admin', 'content_manager'].includes(user?.role ?? '')
);

/**
 * Mirrors PlaylistGroupPolicy::create on the backend — any active user with
 * a recognised role (super_admin, company_admin, content_manager) may create
 * a playlist group. Used to gate the "+ Add New Playlist Group" affordance in
 * pickers so users without permission don't see an option they'd 403 on.
 */
export const selectCanCreatePlaylistGroup = createSelector(
  selectCurrentUser,
  (user) =>
    !!user &&
    user.is_active !== false &&
    ['super_admin', 'company_admin', 'content_manager'].includes(user.role)
);
